import {useEffect, useState} from 'react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Badge} from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {toast} from 'sonner';
import {Plus, Edit, Trash2, Search, X} from 'lucide-react';
import {
    getWithdrawals, createWithdrawal, updateWithdrawal, deleteWithdrawal,
    getAffiliates, getAffiliateMeta
} from '@/api/affiliate.api';

const initialForm = {
    affiliateId: '',
    affiliateName: '',
    amount: '',
    bankName: '',
    ifscCode: '',
    accountNumber: '',
    status: 'Pending',
    transactionRef: '',
    remarks: ''
};

const statusBadgeClass = (s) => ({
    Pending: 'bg-yellow-100 text-yellow-800',
    Approved: 'bg-blue-100 text-blue-700',
    Rejected: 'bg-red-100 text-red-700',
    Paid: 'bg-green-100 text-green-700'
}[s] || 'bg-gray-100 text-gray-700');

const WithdrawalsTab = () => {
    const [list, setList] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(initialForm);

    const load = async () => {
        try {
            const [w, a, m] = await Promise.all([getWithdrawals(), getAffiliates(), getAffiliateMeta()]);
            setList(w.data);
            setAffiliates(a.data);
            setStatuses(m.data.withdrawalStatuses || []);
        } catch {
            toast.error('Failed to load withdrawals');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [w, a, m] = await Promise.all([
                    getWithdrawals(), getAffiliates(), getAffiliateMeta()
                ]);
                if (!mounted) return;
                setList(w.data);
                setAffiliates(a.data);
                setStatuses(m.data.withdrawalStatuses || []);
            } catch {
                if (mounted) toast.error('Failed to load withdrawals');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const openAdd = () => {
        setEditId(null);
        setForm(initialForm);
        setOpen(true);
    };
    const openEdit = (w) => {
        setEditId(w.id);
        setForm({
            affiliateId: w.affiliateId || '',
            affiliateName: w.affiliateName || '',
            amount: w.amount ?? '',
            bankName: w.bankName || '',
            ifscCode: w.ifscCode || '',
            accountNumber: w.accountNumber || '',
            status: w.status || 'Pending',
            transactionRef: w.transactionRef || '',
            remarks: w.remarks || ''
        });
        setOpen(true);
    };

    const onAffiliateChange = (id) => {
        const a = affiliates.find((x) => x.id === id);
        setForm({
            ...form,
            affiliateId: id,
            affiliateName: a?.fullName || '',
            bankName: a?.bankName || form.bankName,
            ifscCode: a?.ifscCode || form.ifscCode,
            accountNumber: a?.accountNumber || form.accountNumber
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.affiliateId) {
            toast.error('Please select an affiliate');
            return;
        }
        if (!form.amount || Number(form.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        const payload = {...form, amount: Number(form.amount) || 0};
        try {
            if (editId) {
                await updateWithdrawal(editId, payload);
                toast.success('Withdrawal updated');
            } else {
                await createWithdrawal(payload);
                toast.success('Withdrawal created');
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this withdrawal?')) return;
        try {
            await deleteWithdrawal(id);
            toast.success('Deleted');
            load();
        } catch {
            toast.error('Delete failed');
        }
    };

    const q = search.trim().toLowerCase();
    const filtered = list.filter((w) => {
        if (statusFilter !== 'all' && w.status !== statusFilter) return false;
        if (!q) return true;
        return (
            (w.affiliateName || '').toLowerCase().includes(q) ||
            (w.transactionRef || '').toLowerCase().includes(q) ||
            (w.accountNumber || '').toLowerCase().includes(q)
        );
    });

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

    if (loading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full"/>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-3">
                <h2 className="text-xl font-semibold">Withdrawal Requests</h2>
                <Button onClick={openAdd} data-testid="withdrawal-add-button">
                    <Plus className="mr-2" size={18}/> Add Withdrawal
                </Button>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative w-full sm:max-w-sm">
                    <Search size={16}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                    <Input placeholder="Search by affiliate, ref, account..." value={search}
                           onChange={(e) => setSearch(e.target.value)} className="pl-9 pr-9"
                           data-testid="withdrawal-search-input"/>
                    {search && (
                        <button type="button" onClick={() => setSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                            <X size={14}/>
                        </button>
                    )}
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-44"><SelectValue/></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="border rounded-lg overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="p-3 text-left">Affiliate</th>
                        <th className="p-3 text-right">Amount</th>
                        <th className="p-3 text-left">Bank</th>
                        <th className="p-3 text-left">Account</th>
                        <th className="p-3 text-left">Requested</th>
                        <th className="p-3 text-left">Processed</th>
                        <th className="p-3 text-left">Txn Ref</th>
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3 text-right">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.map((w) => (
                        <tr key={w.id} className="border-t hover:bg-gray-50">
                            <td className="p-3 font-medium">{w.affiliateName || '-'}</td>
                            <td className="p-3 text-right font-semibold">₹{w.amount}</td>
                            <td className="p-3 text-sm">
                                <div>{w.bankName || '-'}</div>
                                <div className="text-xs text-gray-500 font-mono">{w.ifscCode || ''}</div>
                            </td>
                            <td className="p-3 font-mono text-xs">{w.accountNumber || '-'}</td>
                            <td className="p-3 text-sm">{fmtDate(w.requestedAt)}</td>
                            <td className="p-3 text-sm">{fmtDate(w.processedAt)}</td>
                            <td className="p-3 font-mono text-xs">{w.transactionRef || '-'}</td>
                            <td className="p-3 text-center">
                                <Badge className={`${statusBadgeClass(w.status)} hover:opacity-90`}>{w.status}</Badge>
                            </td>
                            <td className="p-3 text-right flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEdit(w)}>
                                    <Edit size={14}/>
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => onDelete(w.id)}>
                                    <Trash2 size={14}/>
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                {filtered.length === 0 && <p className="text-center text-gray-500 p-8">No withdrawals found.</p>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>{editId ? 'Edit Withdrawal' : 'Add Withdrawal'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={onSubmit} className="space-y-3">
                        <div>
                            <Label>Affiliate *</Label>
                            <Select value={form.affiliateId} onValueChange={onAffiliateChange}>
                                <SelectTrigger><SelectValue placeholder="Select affiliate"/></SelectTrigger>
                                <SelectContent>
                                    {affiliates.map((a) => (
                                        <SelectItem key={a.id} value={a.id}>
                                            {a.fullName || a.couponCode || a.id}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Amount (₹) *</Label>
                                <Input type="number" min="0" required value={form.amount}
                                       onChange={(e) => setForm({...form, amount: e.target.value})}/>
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                                    <SelectTrigger><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Bank Name</Label>
                                <Input value={form.bankName}
                                       onChange={(e) => setForm({...form, bankName: e.target.value})}/>
                            </div>
                            <div>
                                <Label>IFSC Code</Label>
                                <Input value={form.ifscCode}
                                       onChange={(e) => setForm({...form, ifscCode: e.target.value})}/>
                            </div>
                            <div className="col-span-2">
                                <Label>Account Number</Label>
                                <Input value={form.accountNumber}
                                       onChange={(e) => setForm({...form, accountNumber: e.target.value})}/>
                            </div>
                            <div className="col-span-2">
                                <Label>Transaction Ref</Label>
                                <Input value={form.transactionRef}
                                       onChange={(e) => setForm({...form, transactionRef: e.target.value})}/>
                            </div>
                            <div className="col-span-2">
                                <Label>Remarks</Label>
                                <Input value={form.remarks}
                                       onChange={(e) => setForm({...form, remarks: e.target.value})}/>
                            </div>
                        </div>
                        <Button type="submit" className="w-full">
                            {editId ? 'Update Withdrawal' : 'Create Withdrawal'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WithdrawalsTab;
